<script setup>
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { computed, onMounted, reactive, ref, shallowRef, watch } from "vue";
import { useStore } from "vuex";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import SingleSelect from "@/components/forms/SharedComponents/DarkBlueInputs/SingleSelect.vue";
import FormPanel from "@/components/forms/SharedComponents/panel.vue";
import IconBankAccount from "@/components/PaymentRequest/Icons/IconBankAccount.vue";
import IconCreditCard from "@/components/PaymentRequest/Icons/IconCreditCard.vue";
import { useSubmitLock } from "@/composables/useSubmitLock.ts";
import { PAYMENT_TYPES, STATES } from "@/constants";
import { getApiClient } from "@/services/API";

const props = defineProps({
  balance: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: "Initial invoice for your program",
  },
  invoiceId: {
    type: String,
    default: "",
  },
  invoiceLines: {
    type: Array,
    default: [],
  },
  invoicePaymentFeeOptions: {
    type: Object,
    default: {},
  },
  invoiceUrl: {
    type: String,
    default: "",
  },
});

const store = useStore();
const invoiceURL = ref();
const invoiceGenerated = ref(false);

const LOCK_DURATION_IN_SECONDS = 5;

const { isLocked, reset, lock } = useSubmitLock(`submit-lock:${props.invoiceId}`, {
  durationMs: LOCK_DURATION_IN_SECONDS * 60 * 1000,
});

const featureFlags = computed(() => store.state.featureFlags);

const emits = defineEmits(["close:modal", "invoice-updated", "loading"]);

const openInvoice = () => {
  if (invoiceURL.value) {
    window.open(invoiceURL.value, "_blank");
    emits("close:modal");
  }
};

const requiredErrorCopy = "This field is required.";
const invoiceButtonModal = ref(null);
const internalState = reactive({
  submissionError: null,
  paymentType: "",
  selectedPaymentType: null,
  initialPaymentType: null,
  selectedState: null,
});

watch(
  () => internalState.selectedPaymentType,
  () => {
    // Changing payment type resets state on US cc
    internalState.selectedState = null;
  },
);

const paymentFeeOptions = computed(() => {
  const options = [];
  for (const property in props.invoicePaymentFeeOptions) {
    const option = props.invoicePaymentFeeOptions[property];
    option["icon"] =
      option.name === PAYMENT_TYPES.BANK_ACCOUNT.value ? shallowRef(IconBankAccount) : shallowRef(IconCreditCard);
    options.push(option);
  }
  return options;
});

const showStateInput = computed(() => {
  // State dropdown will appear for this payment type
  return internalState.selectedPaymentType?.name === PAYMENT_TYPES.US_CREDIT_CARD.value;
});

const initiallySelectedPaymentType = computed(() => {
  const existingInvoiceFees = props.invoiceLines.find((lineItem) => lineItem.is_transaction_fee);
  if (!existingInvoiceFees) {
    return null;
  }
  for (const property in props.invoicePaymentFeeOptions) {
    const fee_option = props.invoicePaymentFeeOptions[property];
    if (
      fee_option.product_type === existingInvoiceFees.metadata.product_type &&
      fee_option.name === existingInvoiceFees.metadata.product_description
    ) {
      return fee_option;
    }
  }
  return null;
});

const paymentMessage = computed(() => {
  switch (internalState.selectedPaymentType?.name ?? "") {
    case PAYMENT_TYPES.BANK_ACCOUNT.value:
      return "* Paying by ACH is subject to a $3 fee per transaction.\n";
    case PAYMENT_TYPES.US_CREDIT_CARD.value:
      return "* Credit/debit card payments are subject to a 3% convenience fee. Visa, MasterCard, American Express and Discover are accepted.";
    case PAYMENT_TYPES.INTERNATIONAL_CREDIT_CARD.value:
      return "* International Credit/debit card payments are subject to a 4% convenience fee. Visa, MasterCard, American Express and Discover are accepted.";
    default:
      return "* Visa, MasterCard, American Express and Discover are accepted.";
  }
});

// Form validation rules (Vuelidate library)
const rules = {
  selectedPaymentType: {
    required: helpers.withMessage(requiredErrorCopy, required),
  },
  selectedState: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => showStateInput?.value),
    ),
  },
};
const v$ = useVuelidate(rules, internalState);

const isCreditCardFeeExempt = computed(() => {
  const option = internalState.selectedPaymentType;
  if (!option) {
    return false;
  }
  if (
    option.exceptions?.length > 0 &&
    option.exceptions.some((exState) => exState === internalState.selectedState?.label)
  ) {
    return true;
  }
  return false;
});

const showFee = computed(() => {
  return !isCreditCardFeeExempt.value && internalState.selectedPaymentType?.fee_amount > 0;
});

const feeName = computed(() => {
  return internalState.selectedPaymentType?.product_description ?? "";
});

const invoiceAmount = computed(() => {
  const totalAmount = props.balance;
  const totalFees = props.invoiceLines
    .filter((lineItem) => lineItem.is_transaction_fee)
    .map((lineItem) => lineItem.amount)
    .reduce((total, current) => {
      return total + current;
    }, 0);
  return totalAmount - totalFees;
});

const feeAmount = computed(() => {
  if (isCreditCardFeeExempt.value) {
    return 0;
  }
  return internalState.selectedPaymentType?.fee_amount ?? 0;
});

const totalAmount = computed(() => {
  // Price of invoice + Fees
  return invoiceAmount.value + feeAmount.value;
});

const formatAmount = (amountInCents = 0) => {
  // Formats to x.xx USD
  return (amountInCents / 100)
    .toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    })
    .concat(" USD");
};

const isLegacyCheckout = computed(() => {
  return featureFlags.value["legacy-checkout"];
});

const requestPaymentType = computed(() => {
  if (isLegacyCheckout.value) {
    return internalState.selectedPaymentType?.stripe_type;
  }
  return {
    paymentMethodType: internalState.selectedPaymentType?.name,
    stripe: internalState.selectedPaymentType?.stripe_type, // New format
  };
});

const requestObject = computed(() => {
  // Hits Payment service endpoint: /api/v1/invoice/:id/checkout/
  const requestData = {
    invoice_id: props.invoiceId,
    billingState: internalState.selectedState?.label ?? null,
    paymentType: requestPaymentType.value,
  };
  if (isLegacyCheckout.value) {
    requestData.amount = feeAmount.value;
    requestData.description = feeName.value;
  }

  return requestData;
});

const payInvoice = async () => {
  if (
    internalState.initialPaymentType !== null &&
    internalState.initialPaymentType.name === internalState.selectedPaymentType.name &&
    totalAmount.value === props.balance
  ) {
    // invoice payment method is unchanged
    invoiceGenerated.value = true;
    return;
  }

  if (isLocked.value) {
    return;
  }

  invoiceURL.value = null;
  invoiceButtonModal.value.startLoading();
  invoiceGenerated.value = false;
  internalState.submissionError = null;
  emits("loading", true);
  lock();

  const currentInvoiceId = props.invoiceId;

  getApiClient()
    .post("/stripe-invoice/checkout", { ...requestObject.value })
    .then((response) => {
      if (!axios.isAxiosError(response)) {
        const { hosted_invoice_url: stripeInvoiceUrl } = response.data;

        invoiceURL.value = stripeInvoiceUrl;
        invoiceGenerated.value = true;

        emits("invoice-updated", {
          newInvoice: response.data,
          oldInvoiceId: currentInvoiceId,
        });
      }
    })
    .catch((error) => {
      internalState.submissionError = error;
    })
    .finally(() => {
      invoiceButtonModal.value?.stopLoading();
      emits("loading", false);
      reset();
    });
};

onMounted(() => {
  internalState.selectedPaymentType = initiallySelectedPaymentType.value;
  internalState.initialPaymentType = initiallySelectedPaymentType.value;
  if (props.invoiceUrl?.length > 0) {
    invoiceURL.value = props.invoiceUrl;
  }
});
</script>

<template>
  <FormPanel
    id="paymentDets"
    title="Payment Details"
    class="mb-6"
    panel-content-class="text-gray-600 border-gray-200"
    :initially-expanded="true"
  >
    <template #content>
      <div class="bg-teal-light rounded p-5 text-base font-semibold text-gray-650">
        <div class="grid grid-cols-2 gap-y-2">
          <p class="text-left">Invoice Amount</p>
          <p class="text-right">{{ formatAmount(invoiceAmount) }}</p>
          <p v-if="showFee" class="text-left">{{ feeName }}</p>
          <p v-if="showFee" class="text-right">{{ formatAmount(feeAmount) }}</p>
          <p class="font-bold text-xl text-left">Total</p>
          <p class="font-bold text-xl text-right">{{ formatAmount(totalAmount) }}</p>
          <p v-if="paymentMessage" class="text-sm w-full text-gray-650 mt-4 col-span-2 font-normal">
            {{ paymentMessage }}
          </p>
        </div>
      </div>
    </template>
  </FormPanel>

  <FormPanel
    id="paymentType"
    title="Payment Type"
    class="mb-6"
    panel-content-class="text-gray-600 border-gray-200"
    :initially-expanded="!invoiceGenerated"
  >
    <template #content>
      <div class="flex" v-if="isLocked">
        <span class="material-symbols-outlined text-warning-600">
          lock
        </span>
        <p class="ml-2 text-sm">
          Payment method changes are locked for {{ LOCK_DURATION_IN_SECONDS }} minutes. You can still continue with the
          current method.
        </p>
      </div>
      <div class="flex flex-col md:flex-row md:justify-between">
        <div
          v-for="(paymentFeeOption, index) in paymentFeeOptions"
          :key="paymentFeeOption.name"
          class="mt-3 w-full md:w-1/3"
          :class="[{ 'mr-3': index !== 2 }, index ? 'md:w-1/3' : 'md:w-1/4']"
        >
          <input
            :id="paymentFeeOption.name"
            v-model="internalState.selectedPaymentType"
            type="radio"
            name="paymentFeeOption"
            :value="paymentFeeOption"
            class="hidden"
            :disabled="invoiceGenerated"
          />
          <label
            class="flex flex-col p-5 rounded-md w-full h-full"
            :class="[
              paymentFeeOption.name === internalState.selectedPaymentType?.name
                ? 'bg-blue-selection text-white'
                : 'bg-blue-400 text-gray-650',
              invoiceGenerated ? 'cursor-not-allowed' : 'cursor-pointer',
            ]"
            :for="paymentFeeOption.name"
            data-testid="invoice-payment-type-label"
          >
            <component :is="paymentFeeOption.icon" />
            <span class="text-sm mt-2">
              {{ paymentFeeOption.description }}
            </span>
          </label>
        </div>
      </div>
      <div v-if="showStateInput" class="mt-4 block w-full md:w-3/6 xl:w-3/6">
        <!-- State -->
        <SingleSelect
          id="state"
          v-model.trim="internalState.selectedState"
          label="Select Billing State"
          :vuelidate-field="v$.selectedState"
          :options="STATES"
          :get-option-label="(option) => option.label"
          :disabled="invoiceGenerated"
          data-testid="state-select"
        />
      </div>
      <ButtonWithSpinner
        v-if="!invoiceGenerated"
        id="invoiceButtonModal"
        ref="invoiceButtonModal"
        data-cy="invoiceButtonModal"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px] mt-4"
        :grey-disabled-class="true"
        :disabled="v$.$invalid || invoiceGenerated || isLocked"
        data-testid="confirm-payment-method-button"
        @click="payInvoice"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">Confirm Payment Method</span>
        </div>
      </ButtonWithSpinner>
    </template>
  </FormPanel>
  <div v-if="invoiceGenerated" class="text-center mt-6">
    <p class="text-lg font-semibold mb-4">
      Thank you for confirming your payment method.
    </p>
    <ButtonWithSpinner
      type="submit"
      variant="secondary"
      variant-type="block"
      button-height="min-h-[58px] md:min-h-[60px] mt-4"
      :grey-disabled-class="true"
      @click="openInvoice"
      data-testid="continue-to-payment-button"
    >
      <div class="flex items-center justify-center">
        <span class="pr-3 uppercase">Continue to Payment</span>
      </div>
    </ButtonWithSpinner>
  </div>

  <div v-if="internalState.submissionError" class="error text-error-900 mt-2" data-testid="submission-error">
    {{ internalState.submissionError }}
  </div>
</template>
